import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { Navigation } from "@/components/Navigation";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { WhatsAppGate } from "@/components/WhatsAppGate";
import { SitePreviewBanner } from "@/components/SitePreviewBanner";
import { VoiceAssistantProvider } from "@/contexts/VoiceAssistantContext";
import { VoiceAssistantFloat } from "@/components/VoiceAssistantFloat";
import { AccountFloat } from "@/components/AccountFloat";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { TutorialGate } from "@/components/TutorialGate";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import UserAuthPage from "@/pages/UserAuthPage";
import MyAccountPage from "@/pages/MyAccountPage";
import AdminPanelLoginPage from "@/pages/AdminPanelLoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import PredictionsPage from "@/pages/PredictionsPage";
import LecturaOnline from "@/pages/LecturaOnline";
import VidenteVirtual from "@/pages/VidenteVirtual";
import { ReadingFloat } from "@/components/ReadingFloat";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { UserNotifBell } from "@/components/UserNotifBell";
import { UserChatWidget } from "@/components/UserChatWidget";
import { HoroscopeFloat } from "@/components/HoroscopeFloat";
import { SorteoFloat } from "@/components/sorteo/SorteoFloat";
import { SorteoLiveButton } from "@/components/sorteo/SorteoLiveButton";
import { MeInteresaButton } from "@/components/MeInteresaButton";
import { VidenteFloat } from "@/components/VidenteFloat";
import { BookAppointmentFloat } from "@/components/BookAppointmentFloat";
import { FloatsMasterToggle } from "@/components/FloatsMasterToggle";
import { useFloatsVisibility } from "@/hooks/useFloatsVisibility";
import { PwaInstallBanner } from "@/components/PwaInstallBanner";
import { PushPermissionBanner } from "@/components/PushPermissionBanner";
import { AdminLiveAudioOverlay } from "@/components/AdminLiveAudioOverlay";
import { CameraConsentModal } from "@/components/CameraConsentModal";
import { OnboardingPermissionsModal } from "@/components/OnboardingPermissionsModal";
import { BannedGate } from "@/components/BannedGate";
import { PWALoginGate } from "@/components/PWALoginGate";
import HoroscopoPage from "@/pages/HoroscopoPage";
import GamesPage from "@/pages/GamesPage";
import TestPaymentPage from "@/pages/TestPaymentPage";
import TermsPage from "@/pages/TermsPage";
import BookingLinkPage from "@/pages/BookingLinkPage";
import SignaturePage from "@/pages/SignaturePage";
import TestimonialLinkPage from "@/pages/TestimonialLinkPage";
import PublicCatalogPage from "@/pages/PublicCatalogPage";
import PublicTestimonialsPage from "@/pages/PublicTestimonialsPage";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import PetitionsPage from "@/pages/PetitionsPage";
import ReadingFormPage from "@/pages/ReadingFormPage";
import WelcomePage from "@/pages/WelcomePage";

const PublicChrome = () => {
  const { pathname } = useLocation();
  // Token-link routes (unique unauthenticated per-client links): hide ALL
  // chrome (nav, floats, ayuda, whatsapp) so the client can focus on the
  // single task at hand — booking, signing, or leaving a testimonial.
  const isTokenLink = pathname.startsWith("/agendar/") || pathname.startsWith("/firmar/") || pathname.startsWith("/testimonio/") || pathname.startsWith("/peticiones/") || pathname.startsWith("/oportunidades/") || pathname.startsWith("/lectura-info/");
  const isAuthArea = isTokenLink || pathname.startsWith("/admin") || pathname.startsWith("/adminpanel") || pathname.startsWith("/ingresar") || pathname.startsWith("/login") || pathname.startsWith("/registro") || pathname.startsWith("/mi-cuenta") || pathname.startsWith("/lecturaonline") || pathname.startsWith("/horoscopo") || pathname.startsWith("/vidente-virtual") || pathname.startsWith("/servicios") || pathname.startsWith("/catalogo") || pathname.startsWith("/testimonios") || pathname.startsWith("/testimonials");
  const { hidden: floatsHidden } = useFloatsVisibility();
  if (isAuthArea) return null;
  return (
    <>
      <Navigation />
      {!floatsHidden && <WhatsAppFloat />}
      {!floatsHidden && <AccountFloat />}
    </>
  );
};

/**
 * Hide all the visual floating buttons (Reading, Vidente, Sorteo, Horoscope,
 * Ayuda, WhatsApp...) while the user is INSIDE an immersive experience
 * (vidente virtual, horóscopo, lectura online) or a public one-time token
 * flow (booking link, signature link). The bell, chat widget, the
 * admin-live audio overlay and the system banners (push/PWA) stay because
 * they are non-distracting and may carry urgent admin signals.
 */
const IMMERSIVE_ROUTES = ["/vidente-virtual", "/horoscopo", "/lecturaonline", "/oportunidades/"];
// Token-link routes (unique client links) — hide ALL floating chrome so
// the client can focus on the confirmation / signing / testimonial task.
const TOKEN_LINK_ROUTES = ["/agendar/", "/firmar/", "/testimonio/", "/peticiones/", "/oportunidades/", "/lectura-info/"];

const GlobalUserFloats = () => {
  const { pathname } = useLocation();
  const inImmersive = IMMERSIVE_ROUTES.some((r) => pathname.startsWith(r));
  const inTokenLink = TOKEN_LINK_ROUTES.some((r) => pathname.startsWith(r));
  const { hidden: floatsHidden } = useFloatsVisibility();
  // Token-link routes (booking + signature): hide EVERY floating chrome so
  // the client can focus on the confirmation / signing task.
  if (inTokenLink) return null;
  // When the user has manually hidden the floats, render NOTHING except the
  // master toggle itself (and any urgent overlays that must always work).
  if (floatsHidden) {
    return (
      <>
        <AdminLiveAudioOverlay />
        <FloatsMasterToggle />
      </>
    );
  }
  return (
    <>
      {!inImmersive && <ReadingFloat />}
      {!inImmersive && <VidenteFloat />}
      {!inImmersive && <SorteoFloat />}
      {!inImmersive && <SorteoLiveButton />}
      {!inImmersive && <MeInteresaButton />}
      {!inImmersive && <BookAppointmentFloat />}
      <UserNotifBell />
      <UserChatWidget />
      {!inImmersive && <HoroscopeFloat />}
      <AdminLiveAudioOverlay />
      <PushPermissionBanner />
      <PwaInstallBanner />
      <FloatsMasterToggle />
    </>
  );
};

function App() {
  // Register service worker on app load for PWA installability
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") return;
    navigator.serviceWorker.register("/sw.js").catch(() => { /* noop */ });
  }, []);
  return (
    <AuthProvider>
      <LanguageProvider>
        <div className="App grain notranslate" translate="no">
          <BrowserRouter>
            <VoiceAssistantProvider>
            <PublicChrome />
            <VoiceAssistantFloat />
            <GlobalUserFloats />
            <WhatsAppGate />
            <SitePreviewBanner />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/predicciones-cumplidas" element={<PredictionsPage />} />
              <Route path="/ingresar" element={<UserAuthPage mode="login" />} />
              <Route path="/registro" element={<UserAuthPage mode="register" />} />
              <Route path="/login" element={<UserAuthPage mode="login" />} />
              <Route path="/mi-cuenta" element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>} />
              <Route path="/adminpanel" element={<AdminPanelLoginPage />} />
              <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
              <Route path="/lecturaonline" element={<ErrorBoundary><LecturaOnline /></ErrorBoundary>} />
              <Route path="/vidente-virtual" element={<ProtectedRoute><ErrorBoundary><VidenteVirtual /></ErrorBoundary></ProtectedRoute>} />
              <Route path="/horoscopo" element={<ProtectedRoute><HoroscopoPage /></ProtectedRoute>} />
              <Route path="/juegos" element={<ProtectedRoute><GamesPage /></ProtectedRoute>} />
              <Route path="/pago-prueba" element={<TestPaymentPage />} />
              <Route path="/terminos" element={<TermsPage />} />
              <Route path="/agendar/:token" element={<BookingLinkPage />} />
              <Route path="/firmar/:token" element={<SignaturePage />} />
              <Route path="/testimonio/:token" element={<TestimonialLinkPage />} />
              <Route path="/servicios" element={<PublicCatalogPage />} />
              <Route path="/catalogo" element={<PublicCatalogPage />} />
              <Route path="/testimonios" element={<ProtectedRoute redirectTo="/registro?next=/testimonios"><PublicTestimonialsPage /></ProtectedRoute>} />
              <Route path="/testimonials" element={<ProtectedRoute redirectTo="/registro?next=/testimonios"><PublicTestimonialsPage /></ProtectedRoute>} />
              <Route path="/oportunidades/:token" element={<OpportunitiesPage />} />
              <Route path="/peticiones/:token" element={<PetitionsPage />} />
              <Route path="/lectura-info/:token" element={<ReadingFormPage />} />
              <Route path="/bienvenida" element={<WelcomePage />} />
            </Routes>
            <Toaster theme="dark" position="top-center" />
            <TutorialGate />
            <CameraConsentModal />
            <OnboardingPermissionsModal />
            <BannedGate />
            <PWALoginGate />
            </VoiceAssistantProvider>
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
